import { getCookie } from 'cookies-next';
import { IncomingMessage } from 'http';

import { locales, LocalesType, regionToLocaleMap } from '@/config/locales';
import { DEV_LOCALE_COOKIE_KEY } from '@/utils/constants/common';

import { isLanguageAgnosticRequest } from '../httpUtil';

import { getLOB, LOB, LOB_PTV } from './lob-utils';
import { getHeaderValue } from './request-extractor';

const DEFAULT_LOCALE = 'en-us';
const DEFAULT_REGION = 'US';

// Get the first value in the Accept-Language header. ie. en-US,en;q=0.9,it-IT;q=0.8,it;q=0.7 or fr;q=0.9,en-US;q=0.8
const getLocaleFromHeader = (acceptLanguageHeader: string) => {
    return acceptLanguageHeader.split(',')[0].split(';')[0].trim();
};

// If region is detected get locale from config or default to en-US
const getLocaleFromConfig = (region: string, lob: LOB): string => {
    if (region) {
        const lowerRegion = region.toLowerCase();
        return (
            regionToLocaleMap[lob][lowerRegion as keyof LocalesType[typeof lob]] ?? DEFAULT_LOCALE
        );
    }

    return DEFAULT_LOCALE;
};

/**
 * Splits a BCP 47 tag into its language and region subtags.
 */
const parseLocaleTag = (tag: string): { language: string; taggedRegion?: string } | undefined => {
    try {
        const { language, region } = new Intl.Locale(tag);
        return { language: language.toLowerCase(), taggedRegion: region?.toLowerCase() };
    } catch {
        return undefined;
    }
};

/**
 * Resolves the locale for an Accept-Language tag that is not itself supported, in order:
 * the region default when it speaks the same language (a LATAM visitor sending es-MX gets
 * es-419), then the variant for the region named by the tag itself (es-MX from the US also
 * gets es-419), then any configured locale sharing the language segment.
 *
 * Callers pass an already region-qualified tag, so a regionless `es` arrives as `es-{region}`
 * and resolves by region too. Only the language segment is trusted from the tag itself; the
 * region subtag is a hint, never returned verbatim unless it maps to a configured locale.
 *
 * Every branch matches on `{language}-` rather than the bare language segment, so a two-letter
 * language can never prefix-match a longer one.
 *
 * @param searchedLocale a region-qualified locale tag, e.g. `es-mx`
 * @returns the resolved locale, or undefined when the tag is invalid or the language is not
 * configured at all
 */
const getFallbackLocaleForLanguage = (
    searchedLocale: string,
    region: string,
    lob: LOB,
): string | undefined => {
    const parsedTag = parseLocaleTag(searchedLocale);
    if (!parsedTag) {
        return undefined;
    }

    const { language, taggedRegion } = parsedTag;
    const languagePrefix = `${language}-`;

    if (taggedRegion) {
        const regionDefaultLocale = getLocaleFromConfig(region, lob);
        if (regionDefaultLocale.startsWith(languagePrefix)) {
            return regionDefaultLocale;
        }

        const taggedRegionLocale = getLocaleFromConfig(taggedRegion, lob);
        if (taggedRegionLocale.startsWith(languagePrefix)) {
            return taggedRegionLocale;
        }
    }

    return locales[lob].find((locale) => locale.startsWith(languagePrefix));
};

/**
 * Determines the locale to use based on the incoming request. The order of preference is as follows:
 * cookie (eg.PP_LOCALE)
 * browser header (Accept-Language) if not a bot and useHeaderLocale is true
 * default locale for the region/lob from config (eg. US -> P+ -> en-us)
 * @param request - The incoming request
 * @param useHeaderLocale - Whether to use browser locale (for P+: true for signed in user, for PTV: true for all users)
 * @param region - The region to use for default locale mapping
 */
const getLocale = (
    request?: IncomingMessage,
    useHeaderLocale = false,
    region = DEFAULT_REGION,
): string => {
    if (!request) {
        return DEFAULT_LOCALE;
    }

    const isDebug = process.env.APP_DEBUG === 'true';
    const localeFromCookie = <string | undefined>getCookie(DEV_LOCALE_COOKIE_KEY, { req: request });
    // Read Accept-Language only — do not pass the full Cookie header through `new Headers()`;
    // undici rejects Cookie values with Unicode above U+00FF (e.g. OneTrust datestamp U+2019).
    const acceptLanguageHeader = getHeaderValue(request, 'accept-language');

    // Is Local override cookie available.
    if (isDebug && localeFromCookie) {
        return localeFromCookie;
    }

    const lob: LOB = getLOB(request);

    // PTV honors the browser's Accept-Language for ALL users (anonymous included); P+ only when the
    // caller opts in (useHeaderLocale, typically signed-in users). Centralizing the PTV rule here
    // keeps locale resolution uniform across every MFE/context that calls getLocale, rather than
    // each context having to special-case PTV.
    const shouldUseHeaderLocale = useHeaderLocale || lob === LOB_PTV;

    // Use the browser's Accept-Language only for a real user (P+: opted-in; PTV: any).
    // For language-agnostic requests (crawlers / blank Accept-Language) fall through to the
    // region-default locale — same shared signal `getRegion` uses to resolve region by URL.
    if (acceptLanguageHeader && shouldUseHeaderLocale && !isLanguageAgnosticRequest(request)) {
        const userLocale = getLocaleFromHeader(acceptLanguageHeader).toLowerCase();
        // if locale is en-x -> check en-x
        // if locale is en in Y region -> check en-y
        const searchedLocale = (
            userLocale.includes('-') ? userLocale : `${userLocale}-${region}`
        ).toLowerCase();

        const isSupported = locales[lob].includes(searchedLocale);
        if (isSupported) {
            return searchedLocale;
        }

        const fallbackLocale = getFallbackLocaleForLanguage(searchedLocale, region, lob);
        if (fallbackLocale) {
            return fallbackLocale;
        }
    }

    // fallback to region default locale
    return getLocaleFromConfig(region, lob);
};

export { getLocale };
